import {
  faBed,
  faCalendarDays,
  faCar,
  faChevronDown,
  faChevronRight,
  faMugHot,
  faPhone,
  faStar,
  faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  RouteDriverSwapDTO,
  RouteLegDTO,
  RoutePackageDTO,
  RoutePlanWarningEnum,
  RouteRebookingKindEnum,
  RouteRebookingProposalDTO,
} from 'api/model';
import Button from 'components/inputs/Button';
import Checkbox from 'components/inputs/Checkbox';
import useModalStack from 'contexts/modal/useModalStack';
import AvailabilityFormModal from 'pages/Employees/Availability/AvailabilityFormModal';
import React from 'react';
import styled from 'styled-components';
import LegTimeline from './LegTimeline';
import LoadPill from './LoadPill';
import {
  candidateNeedsHotel,
  describeDayBreakShortfall,
  describeEndsBefore,
  describeWholeDay,
  formatClock,
  formatDurationMs,
  GapChoice,
  getSelectedCandidate,
  hasHomeRemedy,
  isPackageBlocked,
  isWarningResolvedByHomeRemedy,
  PackageSelection,
  packageWorkSpanMs,
} from './selection-helpers';
import useElementWidth from 'hooks/useElementWidth';
import useHasAccessToAbsoluteTb from 'hooks/useHasAccessToAbsoluteTb';
import useHasAccessToRoutePlanApply, {
  NO_ROUTE_PLAN_APPLY_ACCESS_TITLE,
} from 'hooks/useHasAccessToRoutePlanApply';
import { packageCostBasisMs } from './tb-language';
import { tbWithRemedies } from './tb-remedies';
import WarningPill, { PillRow, SeverityPill, TbBadge } from './WarningPills';
import CaseRegLink from './CaseRegLink';
import { ConfirmedRebooking, RebookingAnswer } from './rebooking-helpers';
import RebookingTimeline from './RebookingTimeline';
import {
  getCandidateRejectionLabel,
  getManualReasonLabel,
  getManualReasonSeverity,
} from './warning-helpers';
import formatCandidateAnchors from './anchor-helpers';
import CandidateChoicePanel from './CandidateChoicePanel';
import { rejectionsShownWithChoice } from './candidate-choice-helpers';

/** Skiftestransport — paret måste läsas som en enhet, därför en egen tydlig ruta. */
const SwapPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 4px 0;
  padding: 7px 9px;
  border: 1px solid rgb(120, 90, 170);
  border-left-width: 3px;
  border-radius: 4px;
  background: rgb(245, 242, 252);
  font-size: 0.9em;
`;

const SwapTitle = styled.span`
  font-weight: 600;
  color: rgb(80, 55, 130);
`;

const SwapDetail = styled.span`
  color: rgb(90, 90, 90);
`;

export type PackageApplyState =
  | { status: 'idle' }
  | { status: 'applied'; skipped: number }
  | { status: 'failed'; message: string };

const Card = styled.div<{ applied?: boolean; failed?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border: 1px solid
    ${({ applied, failed }) =>
      failed ? 'rgb(255,0,0)' : applied ? 'rgb(0,150,0)' : '#b5b3b35c'};
  border-radius: 4px;
  background: ${({ applied }) => (applied ? 'rgba(0,187,0,0.05)' : '#fff')};
  min-width: 0;
`;

/**
 * Kortets layout väljs efter kortets EGEN bredd, inte fönstrets: hur mycket plats kortet får
 * beror på kartkolumnen bredvid. Brett = allt på en rad som förr. Mellan = vem på första raden,
 * siffrorna på en egen remsa. Smalt = som mellan, men nyckeltalen staplas i ett rutnät.
 */
type CardLayout = 'wide' | 'medium' | 'narrow';

const WIDE_MIN_PX = 1050;
const MEDIUM_MIN_PX = 560;

const cardLayoutFor = (width: number | null): CardLayout => {
  // Före första mätningen: mellanläget, som får plats i alla bredder utan att läcka.
  if (width === null) return 'medium';
  if (width >= WIDE_MIN_PX) return 'wide';
  if (width >= MEDIUM_MIN_PX) return 'medium';
  return 'narrow';
};

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

/** En rad i rubriken. Radbryter hellre än trycker ut kortet åt höger. */
const HeaderRow = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 4px 10px;
  min-width: 0;
`;

/** Skjuter betygsstjärnan till högerkanten av första raden. */
const HeaderSpacer = styled.span`
  flex: 1;
`;

/** Rutt och nyckeltal i mellan- och smalt läge — "vad" under raden om "vem". */
const MetricsStrip = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px 16px;
  padding-top: 6px;
  border-top: 1px dashed #b5b3b35c;
`;

/**
 * Varför paketet ligger i "Behöver input". Sitter ovanför fällningen, till skillnad från
 * varningsraden: orsaken är det enda som gör kortet begripligt i den fliken, och ett ihopfällt
 * kort utan den hade varit ett paket utan förklaring.
 */
const ManualReasonRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

/**
 * Hur många samtal paketet väntar på. Syns även ihopfällt — förslagen sitter på benen, och ett
 * ihopfällt kort visar inga ben.
 */
const RebookingChip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
  padding: 2px 10px;
  border: 1px solid rgb(240, 217, 181);
  border-radius: 999px;
  background: rgb(253, 246, 236);
  color: rgb(122, 74, 6);
  font: inherit;
  font-size: 0.8em;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;

  &:disabled {
    cursor: default;
  }
`;

const ManualReasonLead = styled.span`
  font-size: 0.8em;
  color: rgb(120, 120, 120);
  white-space: nowrap;
`;

const PackageLabel = styled.span`
  font-size: 0.8em;
  color: rgb(120, 120, 120);
  white-space: nowrap;
`;

const Name = styled.span`
  font-weight: 700;
  font-size: 1.1em;
  overflow-wrap: anywhere;
`;

const Rank = styled.span`
  font-size: 0.8em;
  color: rgb(120, 120, 120);
`;

const RateButton = styled.button<{ rated: boolean }>`
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  border: 0;
  background: none;
  padding: 0 2px;
  cursor: pointer;
  font-size: 0.9em;
  color: ${({ rated }) => (rated ? 'rgb(230, 160, 20)' : 'rgb(170, 170, 170)')};

  &:hover {
    color: rgb(230, 160, 20);
  }
`;

const IconLink = styled.button`
  border: 0;
  background: none;
  color: rgb(204, 102, 51);
  cursor: pointer;
  font: inherit;
  padding: 0 2px;
`;

const SwitchLink = styled.button`
  border: 0;
  background: none;
  color: rgb(204, 102, 51);
  cursor: pointer;
  font: inherit;
  font-size: 0.85em;
  text-decoration: underline;
  padding: 0;
`;

const Totals = styled.div<{ layout: CardLayout }>`
  display: ${({ layout }) => (layout === 'narrow' ? 'grid' : 'flex')};
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  flex-wrap: wrap;
  gap: 6px 16px;
  margin-left: ${({ layout }) => (layout === 'narrow' ? '0' : 'auto')};
  white-space: nowrap;
  ${({ layout }) => layout === 'narrow' && 'width: 100%;'}
`;

/** Etikett och värde hålls ihop, och bryts aldrig isär när remsan radbryter. */
const Total = styled.div<{ layout: CardLayout }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ layout }) =>
    layout === 'narrow' ? 'flex-start' : 'flex-end'};
`;

const TotalLabel = styled.span`
  font-size: 0.75em;
  color: rgb(120, 120, 120);
`;

const TotalValue = styled.span`
  font-weight: 700;
`;

const StayoverBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 4px;
  background: rgba(0, 122, 255, 0.08);
  color: rgb(0, 90, 190);
  font-size: 0.9em;
`;

/** Föraren hinner inte till sitt befintliga uppdrag efter paketet — bara möjligt vid manuellt val. */
const LateBanner = styled(StayoverBanner)`
  background: rgba(220, 38, 38, 0.08);
  color: rgb(185, 28, 28);
`;

const HotelConfirmRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 4px;
  background: rgba(255, 191, 0, 0.12);
  font-size: 0.9em;
`;

const StatusBanner = styled.div<{ failed?: boolean }>`
  padding: 6px 10px;
  border-radius: 4px;
  font-size: 0.9em;
  font-weight: 600;
  background: ${({ failed }) =>
    failed ? 'rgba(255,0,0,0.08)' : 'rgba(0,187,0,0.12)'};
  color: ${({ failed }) => (failed ? 'rgb(200,0,0)' : 'rgb(0,110,0)')};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  border-top: 1px solid #b5b3b35c;
  padding-top: 8px;
`;

const FooterNote = styled.span`
  font-size: 0.85em;
  color: rgb(120, 120, 120);
`;

const NoCandidate = styled.div`
  color: rgb(120, 120, 120);
`;

/**
 * Svaret på "varför står inte Anna här?". Backend räknar avvisningarna per orsak i stället
 * för per person — antalet räcker för att skilja ett datafel (alla föll på behörighet) från
 * en trång dag (alla var redan bokade), utan att lägga ut personallistor i förslaget.
 */
const RejectionList = styled.div`
  margin-top: 4px;
  font-size: 0.85em;
  color: rgb(120, 120, 120);
`;

/** Smalt läge: bortfallet kostar annars tre rader överst i kortet. */
const RejectionDetails = styled.details`
  margin-top: 4px;
  font-size: 0.85em;
  color: rgb(120, 120, 120);

  summary {
    cursor: pointer;
  }
`;

const BreakSuggestionRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 4px;
  background: rgba(0, 187, 0, 0.08);
  color: rgb(0, 110, 0);
  font-size: 0.9em;
`;

/** Förklarar varför tidslinjens delrastar summeras till EN rasttid vid verkställ. */
const BreakNote = styled.div`
  margin-top: 2px;
  color: rgb(90, 120, 90);
  font-size: 0.92em;
`;

const ResolvedPill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 0.85em;
  font-weight: 600;
  white-space: nowrap;
  background: rgba(0, 187, 0, 0.15);
  color: rgb(0, 110, 0);
`;

const RouteSummary = styled.span`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 12px;
  color: rgb(90, 90, 90);
`;

const LegCount = styled.span`
  color: rgb(140, 140, 140);
`;

const CollapseToggle = styled.button`
  border: none;
  background: none;
  padding: 0 2px;
  margin-right: 2px;
  color: rgb(120, 120, 120);
  cursor: pointer;
  font-size: 12px;
  line-height: 1;

  &:hover {
    color: rgb(60, 60, 60);
  }
`;

/**
 * Ruttsammanfattningen som ersätter tidslinjen i kollapsat läge. Ett skifte gör att paketets
 * rutt vänder vid bytespunkten, så orterna får inte läsas ur första och sista benet — då stod
 * "Stockholm → Malmö" på en dag som slutar i Stockholm.
 */
const describeRoute = (
  pkg: RoutePackageDTO,
  swap?: RouteDriverSwapDTO
): string => {
  const first = pkg.legs[0];
  if (!first) return '';

  if (swap) {
    return `${first.fromCity} → ${swap.pointCity} → ${first.fromCity}`;
  }

  const last = pkg.legs[pkg.legs.length - 1];
  return `${first.fromCity} → ${last.toCity}`;
};

interface Props {
  pkg: RoutePackageDTO;
  /** Satt när paketet ingår i en skiftestransport — paret verkställs alltid tillsammans. */
  driverSwap?: RouteDriverSwapDTO;
  planDate: Date;
  selection: PackageSelection;
  applyState: PackageApplyState;
  isApplying: boolean;
  onSelectionChange: (update: Partial<PackageSelection>) => void;
  onGapChoice: (gap: 'first' | 'last', choice: GapChoice) => void;
  onTransferGapChoice?: (assignmentId: number, choice: GapChoice) => void;
  onApplySingle: () => void;
  onSwitchFieldTester: () => void;
  /** Låser ett ben för manuell planering — samma åtgärd som i den manuella listan. */
  onReserveLeg?: (leg: RouteLegDTO) => void;
  onHoverChange?: (hovered: boolean) => void;
  selectedAnnonsByDestination?: Record<number, number>;
  /** Kollapsat läge visar bara sammanfattningsraden. Utfällt är default. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /**
   * Paketet ligger i "Behöver input" utan egen orsak — alltså för att dess SKIFTESPARTNER gör
   * det. partitionByPlanningClass håller ihop paret (ett halvt verkställt skifte lämnar en förare
   * vid bytespunkten utan bil hem), och utan den här flaggan hade halvan renderats som ett kort i
   * fliken utan att säga varför den låg där.
   */
  followsManualSwapPartner?: boolean;
  /** Öppnar betygsrutan för det här förslaget. */
  onRate?: () => void;
  /** Betyget planeraren redan skickat för paketet i den här körningen. */
  givenRating?: number;
  /** Svar på ett ombokningsförslag (ja, annan tid, nej). Utan den visas inga förslag. */
  onRebookingAnswer?: (
    proposal: RouteRebookingProposalDTO,
    answer: RebookingAnswer
  ) => void;
  /** Uppdraget vars ombokningssvar just nu skickas. */
  rebookingBusyAssignmentId?: number;
  /** Ja som redan är sparade i körningen, per uppdrag — benen visas som bekräftade. */
  confirmedRebookings?: Record<number, ConfirmedRebooking>;
  /** Varför samåkningen hem spärras eller hemresegruppen inte gäller (describeHomeRideStatus). */
  homeRideNotice?: string;
}

const PackageCard: React.FC<Props> = ({
  pkg,
  driverSwap,
  planDate,
  selection,
  applyState,
  isApplying,
  onSelectionChange,
  onGapChoice,
  onTransferGapChoice,
  onApplySingle,
  onSwitchFieldTester,
  onReserveLeg,
  onHoverChange,
  selectedAnnonsByDestination,
  collapsed = false,
  onToggleCollapsed,
  followsManualSwapPartner = false,
  onRate,
  givenRating,
  onRebookingAnswer,
  rebookingBusyAssignmentId,
  confirmedRebookings,
  homeRideNotice,
}) => {
  const modalStack = useModalStack();
  const { width: cardWidth, ref: cardRef } = useElementWidth();
  const layout = cardLayoutFor(cardWidth);
  const showAbsoluteTb = useHasAccessToAbsoluteTb();
  const candidate = getSelectedCandidate(pkg, selection);
  const wholeDay = candidate ? describeWholeDay(candidate) : null;
  const endsBefore = candidate ? describeEndsBefore(candidate) : null;
  const dayBreakShortfall = candidate
    ? describeDayBreakShortfall(candidate)
    : null;
  const applied = applyState.status === 'applied';
  const blocked = isPackageBlocked(pkg, selection, driverSwap);
  const needsHotel = candidateNeedsHotel(candidate, selection, driverSwap);
  const interactionDisabled = applied || isApplying;
  const packageNumber = pkg.packageIndex + 1;
  // Paketets TB med de VALDA lucka-åtgärderna inräknade. Backend räknar bara benen — och det är
  // persontransporten och hotellet som gör obygden dyr, så utan detta var talet tystast där det
  // behövdes mest.
  const adjustedTb = tbWithRemedies({
    pkg,
    candidate,
    selection,
    // Hotellkostnaden finns oavsett om övernattningen är BEKRÄFTAD — hotelConfirmed säger att den
    // är ordnad, inte att den är gratis.
    needsHotel,
  });

  // Utan skrivbehörighet (GlobalSettingEnum 106) syns knappen men går inte att trycka på.
  const canApply = useHasAccessToRoutePlanApply();

  // Hemrese-varningar (slutar utanför hemområdet, hotell) räknas som lösta när en
  // åtgärd för sista benet är vald — persontransporten/utfyllnaden tar FT:n hemåt.
  // Rastvarningen ersätts av planerarens rastförslag (verkställs med tilldelningen).
  const visibleCandidateWarnings =
    candidate?.warnings.filter(
      (w) =>
        !isWarningResolvedByHomeRemedy(w, selection, driverSwap) &&
        !(
          w === RoutePlanWarningEnum.BreakTimeInsufficient &&
          candidate.breakSuggestion
        )
    ) ?? [];
  // Paketets egna varningar bär samma hemrese-frågor som kandidatens och måste filtreras
  // likadant — annars står "Slutar utanför hemområdet" kvar intill en skiftesruta som säger
  // att båda är hemma samma kväll.
  const visiblePackageWarnings = pkg.warnings.filter(
    (w) => !isWarningResolvedByHomeRemedy(w, selection, driverSwap)
  );
  const homeRemedyResolvedSomething =
    !!candidate &&
    candidate.warnings.some((w) =>
      isWarningResolvedByHomeRemedy(w, selection, driverSwap)
    );

  const candidateCount = pkg.candidates.length;
  const rejectionSummary = pkg.candidateRejections
    .filter((r) => r.count > 0)
    .map((r) => `${r.count} ${getCandidateRejectionLabel(r.reason)}`)
    .join(', ');
  const rejectedCount = pkg.candidateRejections.reduce(
    (sum, r) => sum + r.count,
    0
  );
  // Under jämförelsen: utan dem som tabellen redan visar som lediga nu.
  const choiceRejections = rejectionsShownWithChoice(pkg).filter(
    (r) => r.count > 0
  );
  const choiceRejectionSummary = choiceRejections
    .map((r) => `${r.count} ${getCandidateRejectionLabel(r.reason)}`)
    .join(', ');
  const choiceRejectedCount = choiceRejections.reduce(
    (sum, r) => sum + r.count,
    0
  );
  const currentRank = candidate?.rank ?? 0;
  // Benen som bär reserverad rast. Fler än ett ⇒ tidslinjens delrastar (30 + 30) skiljer sig från
  // den ENA rasttid apply skriver, och skillnaden måste förklaras i kortet.
  const breakLegs = pkg.legs.filter((l) => l.breakMinutes > 0);

  const openRebookings = onRebookingAnswer
    ? (pkg.rebookingProposals ?? []).filter(
        (r) => confirmedRebookings?.[r.assignmentId] === undefined
      )
    : [];
  const doneRebookings = pkg.legs.filter(
    (l) => confirmedRebookings?.[l.assignmentId] !== undefined
  ).length;
  // Håltimmesförslagen är rekommendationer: paketet går att köra som det är.
  const rebookingsOptional =
    openRebookings.length > 0 &&
    openRebookings.every((r) => r.kind === RouteRebookingKindEnum.ShortenWait);
  const showManualReasons =
    pkg.manualReasons.length > 0 || followsManualSwapPartner;

  const openAvailabilityModal = () => {
    if (!candidate) return;
    modalStack.push(
      <AvailabilityFormModal
        initialDate={new Date(planDate)}
        name={candidate.fullName}
        onClose={() => modalStack.pop()}
        onSaved={() => modalStack.pop()}
        userId={candidate.userId}
        viewOnly
      />
    );
  };

  const candidateDetails = candidate && (
    <>
      {/* Ankaret, inte bostadsorten: homeCity är fälttestarens folkbokförda ort, och
          planeringen räknar inte från den (§ 1.3). Rubriken sa "Hem · Upplands väsby" om en
          dag som börjar vid Stockholm C. Med flera ankare visas start → slut. */}
      <PackageLabel>{formatCandidateAnchors(candidate)}</PackageLabel>
      <Rank>
        {currentRank === 0
          ? 'manuellt vald'
          : `rank ${currentRank} av ${candidateCount}`}
      </Rank>
      {/* Belastningen syns där valet visas. Rangordningen är fortfarande ren närhet —
          pillret är underlag för trafikledarens eget byte, inte en urvalsgrund. */}
      <LoadPill
        availabilityWindow={candidate.availabilityWindow}
        hoursLast28Days={candidate.hoursLast28Days}
        loadPercent={candidate.loadPercent}
        maxHoursPer4Weeks={candidate.maxHoursPer4Weeks}
      />
      <SwitchLink
        disabled={interactionDisabled}
        onClick={onSwitchFieldTester}
        type="button"
      >
        Byt fälttestare
      </SwitchLink>
    </>
  );

  const routeSummary = (
    <RouteSummary>
      {describeRoute(pkg, driverSwap)}
      <LegCount>
        {driverSwap ? pkg.legs.length * 2 : pkg.legs.length} ben
      </LegCount>
    </RouteSummary>
  );

  const totals = (
    <Totals layout={layout}>
      <Total layout={layout}>
        <TotalLabel>Arbetstid</TotalLabel>
        <TotalValue title="Från första upphämtningens start till sista avlämningens slut. Överföringar, väntan och rast däremellan ingår; pendling ut och hemresa gör det inte.">
          {formatDurationMs(packageWorkSpanMs(pkg))}
        </TotalValue>
      </Total>
      <Total layout={layout}>
        <TotalLabel>Uppdragstid</TotalLabel>
        <TotalValue title="Summan av uppdragens estimerade tider, utan överföringar, väntan och rast.">
          {formatDurationMs(pkg.totalOccupiedMs)}
        </TotalValue>
      </Total>
      {pkg.totalBreakMs > 0 && (
        <Total layout={layout}>
          <TotalLabel>Rast</TotalLabel>
          <TotalValue title="Rast som kedjebygget reserverat. Ligger inne i paketets spann och skjuter fram allt efter sig, men räknas inte som arbetstid eller kostnad.">
            {formatDurationMs(pkg.totalBreakMs)}
          </TotalValue>
        </Total>
      )}
      <Total layout={layout}>
        <TotalLabel>Tomkörning</TotalLabel>
        <TotalValue>{pkg.totalTransferKm} km</TotalValue>
      </Total>
      <Total layout={layout}>
        <TotalLabel>TB</TotalLabel>
        <TbBadge
          align={layout === 'narrow' ? 'left' : 'right'}
          bufferPercent={adjustedTb.bufferPercent ?? undefined}
          costBasisMs={packageCostBasisMs(pkg)}
          costSek={pkg.costSek}
          durationIsEstimated={pkg.legs.some((l) => l.durationIsEstimated)}
          estimated={pkg.tbIsEstimated}
          hotelUnpriced={adjustedTb.hotelUnpriced}
          level={adjustedTb.level}
          remedyCostSek={adjustedTb.remedyCostSek}
          remedyRevenueSek={adjustedTb.remedyRevenueSek}
          revenueSek={pkg.revenueSek}
          scope="package"
          showAbsolute={showAbsoluteTb}
          unpricedRemedy={adjustedTb.hasUnpricedRemedy}
        />
      </Total>
    </Totals>
  );

  const rateButton = onRate && (
    <RateButton
      onClick={onRate}
      rated={givenRating !== undefined}
      title={
        givenRating !== undefined
          ? `Du gav förslaget ${givenRating}/5 — klicka för att betygsätta igen`
          : 'Betygsätt ruttförslaget'
      }
      type="button"
    >
      <FontAwesomeIcon icon={faStar} />
      {givenRating !== undefined && <span>{givenRating}</span>}
    </RateButton>
  );

  return (
    <Card
      ref={cardRef}
      applied={applied}
      failed={applyState.status === 'failed'}
      onMouseEnter={() => onHoverChange?.(true)}
      onMouseLeave={() => onHoverChange?.(false)}
    >
      <Header>
        <HeaderRow>
          <Checkbox
            checked={selection.includePackage && !blocked}
            disabled={interactionDisabled || blocked || !candidate}
            onChange={(e) =>
              onSelectionChange({ includePackage: e.currentTarget.checked })
            }
          />
          {onToggleCollapsed && (
            <CollapseToggle
              aria-expanded={!collapsed}
              aria-label={
                collapsed ? 'Visa paketets rutt' : 'Dölj paketets rutt'
              }
              onClick={onToggleCollapsed}
              type="button"
            >
              <FontAwesomeIcon
                icon={collapsed ? faChevronRight : faChevronDown}
              />
            </CollapseToggle>
          )}
          <PackageLabel>Paket {packageNumber}</PackageLabel>
          {candidate ? (
            <>
              <Name>{candidate.fullName}</Name>
              <IconLink
                onClick={openAvailabilityModal}
                title={`Visa tillgänglighet och bokade uppdrag för ${candidate.fullName}`}
                type="button"
              >
                <FontAwesomeIcon icon={faCalendarDays} />
              </IconLink>
            </>
          ) : (
            <Name>Ingen kandidat</Name>
          )}
          {layout === 'wide' ? (
            <>
              {candidateDetails}
              {routeSummary}
              {totals}
            </>
          ) : (
            <HeaderSpacer />
          )}
          {rateButton}
        </HeaderRow>
        {layout !== 'wide' && (
          <>
            {candidate && <HeaderRow>{candidateDetails}</HeaderRow>}
            <MetricsStrip>
              {routeSummary}
              {totals}
            </MetricsStrip>
          </>
        )}
      </Header>

      {(showManualReasons || openRebookings.length > 0) && (
        <ManualReasonRow>
          {showManualReasons && <ManualReasonLead>Behöver dig:</ManualReasonLead>}
          {pkg.manualReasons.map((reason) => (
            <SeverityPill
              key={reason}
              severity={getManualReasonSeverity(reason)}
            >
              {getManualReasonLabel(reason)}
            </SeverityPill>
          ))}
          {pkg.manualReasons.length === 0 && followsManualSwapPartner && (
            <SeverityPill severity="info">
              Följer skiftespartnern — verkställs tillsammans
            </SeverityPill>
          )}
          {openRebookings.length > 0 && (
            <RebookingChip
              disabled={!collapsed}
              onClick={collapsed ? onToggleCollapsed : undefined}
              title={
                collapsed
                  ? 'Visa benen som ska bokas om'
                  : 'Ring och svara på benen nedan'
              }
              type="button"
            >
              <FontAwesomeIcon icon={faPhone} />
              {openRebookings.length} samtal
              {rebookingsOptional ? ' · valfritt' : ''}
              {doneRebookings > 0 ? ` · ${doneRebookings} klart` : ''}
            </RebookingChip>
          )}
        </ManualReasonRow>
      )}

      {!collapsed && (
        <>
          {(visiblePackageWarnings.length > 0 ||
            visibleCandidateWarnings.length > 0 ||
            homeRemedyResolvedSomething ||
            adjustedTb.hasUnpricedRemedy) && (
            <PillRow>
              {visiblePackageWarnings.map((w) => (
                <WarningPill key={`p${w}`} warning={w} />
              ))}
              {visibleCandidateWarnings.map((w) => (
                <WarningPill key={`c${w}`} warning={w} />
              ))}
              {homeRemedyResolvedSomething && hasHomeRemedy(selection) && (
                <ResolvedPill>✓ Hemresa löst via vald åtgärd</ResolvedPill>
              )}
              {/* Att TB tystnar är ingen liten sak: den valda åtgärden går inte att väga mot de andra,
              och orsaken är åtgärdbar (lägg in prisraden). Grå text i en hover räcker inte — det
              här är ett beslutsläge och hör i varningsraden. */}
              {adjustedTb.hasUnpricedRemedy && (
                <SeverityPill severity="decision">
                  <FontAwesomeIcon icon={faTriangleExclamation} />
                  TB okänt — vald lucka-åtgärd saknar prisrad
                </SeverityPill>
              )}
            </PillRow>
          )}

          {driverSwap &&
            (() => {
              const isSideA =
                pkg.packageIndex === driverSwap.sideA.packageIndex;
              const mine = isSideA ? driverSwap.sideA : driverSwap.sideB;
              const other = isSideA ? driverSwap.sideB : driverSwap.sideA;
              return (
                <SwapPanel>
                  <SwapTitle>
                    ⇄ Skiftestransport med paket {other.packageIndex + 1} — byte
                    på {driverSwap.pointName}, {driverSwap.pointCity} kl{' '}
                    {formatClock(driverSwap.meetingTime)}
                  </SwapTitle>
                  <SwapDetail>
                    {mine.fieldTesterName ?? 'Fälttestaren'} kör{' '}
                    {mine.registrationNumber} från {mine.fromCity} till
                    bytespunkten och tar {mine.returnRegistrationNumber} hem
                    till {mine.returnToCity}.{' '}
                    {other.fieldTesterName ?? 'Motparten'} gör omvänt.
                  </SwapDetail>
                  <SwapDetail>
                    Båda är hemma samma kväll — ingen övernattning och ingen
                    persontransport. Paketen markeras och verkställs
                    tillsammans.
                  </SwapDetail>
                </SwapPanel>
              );
            })()}

          {candidate?.startsAfterAssignmentId && (
            <StayoverBanner>
              <FontAwesomeIcon icon={faCar} />
              Startar där sitt befintliga uppdrag i dag slutar —{' '}
              {candidate.stayoverCity}
              {candidate.stayoverAddress
                ? `, ${candidate.stayoverAddress}`
                : ''}
            </StayoverBanner>
          )}

          {endsBefore &&
            (endsBefore.late ? (
              <LateBanner>
                <FontAwesomeIcon icon={faCar} />
                {endsBefore.text}
              </LateBanner>
            ) : (
              <StayoverBanner>
                <FontAwesomeIcon icon={faCar} />
                {endsBefore.text}
              </StayoverBanner>
            ))}

          {wholeDay && (
            <StayoverBanner>
              <FontAwesomeIcon icon={faCalendarDays} />
              {wholeDay}
            </StayoverBanner>
          )}

          {dayBreakShortfall && (
            <StayoverBanner>
              <FontAwesomeIcon icon={faMugHot} />
              {dayBreakShortfall}
            </StayoverBanner>
          )}

          {candidate?.startsFromStayover && (
            <StayoverBanner>
              <FontAwesomeIcon icon={faBed} />
              Startar från övernattning i {candidate.stayoverCity} —{' '}
              {candidate.stayoverAddress}
            </StayoverBanner>
          )}

          {candidate?.breakSuggestion && (
            <BreakSuggestionRow>
              <FontAwesomeIcon icon={faMugHot} />
              <div>
                Rast {candidate.breakSuggestion.breakMinutes} min planeras på{' '}
                <CaseRegLink caseId={candidate.breakSuggestion.caseId}>
                  {candidate.breakSuggestion.registrationNumber ||
                    `uppdrag ${candidate.breakSuggestion.assignmentId}`}
                </CaseRegLink>{' '}
                — läggs på automatiskt vid verkställ.
                {breakLegs.length > 1 && (
                  <BreakNote title="Rasttid är ett fält per uppdrag (Assignment.BreakTime), så hela reservationen skrivs på det första uppdrag som bär en rast. Tidslinjen visar var tiden faktiskt ligger i dagen — summan är densamma.">
                    Tidslinjen visar{' '}
                    {breakLegs.map((l) => `${l.breakMinutes}`).join(' + ')} min
                    på {breakLegs.length} ben; hela reservationen skrivs som en
                    rasttid på det första av dem.
                  </BreakNote>
                )}
              </div>
            </BreakSuggestionRow>
          )}

          {candidate &&
            !candidate.policyOk &&
            candidate.nextPossibleStartTime && (
              <StatusBanner failed>
                Tidigast möjlig start{' '}
                {new Date(candidate.nextPossibleStartTime).toLocaleString(
                  'sv-SE',
                  {
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  }
                )}{' '}
                — körtidsreglerna är inte uppfyllda före dess.
              </StatusBanner>
            )}

          {!candidate && (
            <NoCandidate>
              Ingen fälttestare är tillgänglig eller inom räckhåll. Paketet kan
              inte verkställas utan fälttestare.
              {rejectionSummary && (
                <RejectionList>Bortfallet: {rejectionSummary}.</RejectionList>
              )}
            </NoCandidate>
          )}

          {/* Varför just hen, och vilka andra som fanns (ruttfeedback 2026-10-28). Bortfallet
              per orsak ligger under jämförelsen i stället för som egen rad överst. */}
          {candidate && candidate.rank === 1 && (
            <CandidateChoicePanel
              footer={
                choiceRejectionSummary && (
                  <RejectionList>
                    Ej valbara: {choiceRejectedCount} ({choiceRejectionSummary}).
                  </RejectionList>
                )
              }
              packageLabel={(index) => `paket ${index + 1}`}
              pkg={pkg}
            />
          )}
          {/* Bytt fälttestare: förklaringen gäller förslagets rank 1, inte hen. */}
          {candidate &&
            candidate.rank !== 1 &&
            rejectionSummary &&
            layout !== 'narrow' && (
              <RejectionList>
                Övriga fälttestare: {rejectionSummary}.
              </RejectionList>
            )}
          {candidate &&
            candidate.rank !== 1 &&
            rejectionSummary &&
            layout === 'narrow' && (
            <RejectionDetails>
              <summary>Övriga fälttestare: {rejectedCount} ej valbara</summary>
              {rejectionSummary}.
            </RejectionDetails>
          )}

          <LegTimeline
            onReserveLeg={onReserveLeg}
            applyDisabled={interactionDisabled}
            candidate={candidate}
            driverSwap={driverSwap}
            onGapChoice={onGapChoice}
            onTransferGapChoice={onTransferGapChoice}
            pkg={pkg}
            homeRideNotice={homeRideNotice}
            rebooking={
              onRebookingAnswer && {
                disabled: !canApply,
                disabledTitle: NO_ROUTE_PLAN_APPLY_ACCESS_TITLE,
                busyAssignmentId: rebookingBusyAssignmentId,
                confirmed: confirmedRebookings ?? {},
                onAnswer: onRebookingAnswer,
              }
            }
            selectedAnnonsByDestination={selectedAnnonsByDestination}
            selection={selection}
          />

          {openRebookings.length > 0 && <RebookingTimeline pkg={pkg} />}

          {needsHotel && candidate && (
            <HotelConfirmRow>
              <Checkbox
                checked={selection.hotelConfirmed}
                disabled={interactionDisabled}
                onChange={(e) =>
                  onSelectionChange({ hotelConfirmed: e.currentTarget.checked })
                }
              >
                Övernattning är ordnad — markera {candidate.fullName}s sista
                uppdrag med hotellövernattning
              </Checkbox>
            </HotelConfirmRow>
          )}

          {applyState.status === 'applied' && (
            <StatusBanner>
              Verkställd
              {applyState.skipped > 0 &&
                ` — ${applyState.skipped} rad(er) hoppades över`}
            </StatusBanner>
          )}
          {applyState.status === 'failed' && (
            <StatusBanner failed>
              Verkställan misslyckades: {applyState.message}
            </StatusBanner>
          )}

          {candidate && !applied && (
            <Footer>
              <FooterNote>
                {/* Skiftet delar varje uppdrag i två vid bytespunkten (ApplyDriverSwapAsync), så
                paketet skriver dubbelt så många rader som det har ben. */}
                {driverSwap ? pkg.legs.length * 2 : pkg.legs.length} uppdrag
                sätts till Planerad
                {needsHotel && !selection.hotelConfirmed
                  ? ' — bekräfta övernattningen innan paketet kan verkställas'
                  : ''}
              </FooterNote>
              <span
                title={canApply ? undefined : NO_ROUTE_PLAN_APPLY_ACCESS_TITLE}
              >
                <Button
                  disabled={!canApply || blocked || isApplying}
                  onClick={onApplySingle}
                  type="button"
                >
                  Verkställ paket {packageNumber}
                </Button>
              </span>
            </Footer>
          )}
        </>
      )}
    </Card>
  );
};

export default PackageCard;
